import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CameraDragList from './CameraDragList';
import CameraSelectionDialog from './CameraSelectionDialog';

const GRID_OPTIONS = [
  { value: '1x1', label: '1x1 (1 camera)' },
  { value: '2x2', label: '2x2 (4 cameras)' },
  { value: '3x3', label: '3x3 (9 cameras)' },
  { value: '4x4', label: '4x4 (16 cameras)' },
  { value: '2x3', label: '2x3 (6 cameras)' },
  { value: '3x4', label: '3x4 (12 cameras)' },
];

function CameraSetForm({
  open,
  onClose,
  onSave,
  set,
  availableCameras,
  camerasLoading,
  saving,
  error,
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    grid_size: '3x3',
    is_default: false,
  });
  const [cameras, setCameras] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);

  // Initialize form when set changes or dialog opens
  useEffect(() => {
    if (open) {
      if (set) {
        setFormData({
          name: set.name || '',
          description: set.description || '',
          grid_size: set.grid_size || '3x3',
          is_default: set.is_default || false,
        });
        setCameras(
          (set.cameras || []).map((c, index) => ({
            camera_id: c.camera_id,
            site_id: c.site_id,
            position: c.position ?? index,
            name: c.camera_name,
            display_name: c.camera_display_name,
            site_name: c.site_name,
            site_display_name: c.site_display_name,
          }))
        );
      } else {
        setFormData({
          name: '',
          description: '',
          grid_size: '3x3',
          is_default: false,
        });
        setCameras([]);
      }
      setValidationErrors({});
    }
  }, [open, set]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleCamerasReorder = (reorderedCameras) => {
    setCameras(reorderedCameras);
  };

  const handleCameraRemove = (cameraToRemove) => {
    setCameras((prev) =>
      prev
        .filter(
          (c) =>
            !(c.camera_id === cameraToRemove.camera_id && c.site_id === cameraToRemove.site_id)
        )
        .map((c, index) => ({ ...c, position: index }))
    );
  };

  const handleCamerasAdd = (newCameras) => {
    const startPosition = cameras.length;
    const toAdd = newCameras.map((camera, index) => ({
      camera_id: camera.id,
      site_id: camera.site_id,
      position: startPosition + index,
      name: camera.name,
      display_name: camera.display_name,
      site_name: camera.site_name,
      site_display_name: camera.site_display_name,
    }));
    setCameras((prev) => [...prev, ...toAdd]);
  };

  const validate = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.grid_size) {
      errors.grid_size = 'Grid size is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const data = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      grid_size: formData.grid_size,
      is_default: formData.is_default,
      camera_ids: cameras.map((c) => ({
        camera_id: c.camera_id,
        site_id: c.site_id,
        position: c.position,
      })),
    };

    onSave(data);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      grid_size: '3x3',
      is_default: false,
    });
    setCameras([]);
    setValidationErrors({});
    onClose();
  };

  const isEditing = !!set;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Camera Set' : 'Create Camera Set'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleChange('name')}
              fullWidth
              required
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              autoFocus
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={2}
            />

            <FormControl fullWidth error={!!validationErrors.grid_size}>
              <InputLabel>Grid Size</InputLabel>
              <Select
                value={formData.grid_size}
                onChange={handleChange('grid_size')}
                label="Grid Size"
              >
                {GRID_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_default}
                  onChange={handleChange('is_default')}
                />
              }
              label="Set as default"
            />

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">
                  Cameras ({cameras.length})
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCameraDialogOpen(true)}
                >
                  Add Cameras
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Drag to reorder. Order determines display position in video wall.
              </Typography>
              <CameraDragList
                cameras={cameras}
                onReorder={handleCamerasReorder}
                onRemove={handleCameraRemove}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Set'}
          </Button>
        </DialogActions>
      </Dialog>

      <CameraSelectionDialog
        open={cameraDialogOpen}
        onClose={() => setCameraDialogOpen(false)}
        onSelect={handleCamerasAdd}
        availableCameras={availableCameras}
        selectedCameras={cameras}
        loading={camerasLoading}
      />
    </>
  );
}

export default CameraSetForm;
